import React from 'react';
import PropTypes from 'prop-types';
import { LuInfo } from 'react-icons/lu';

/**
 * StatStrip — a flat row of read-only figures separated by hairlines.
 *
 * The counterpart to `StatTile` (dashboards): where a tile is a card that can
 * carry a trend and be clicked, a strip is a quiet reference row inside a page
 * that already has a focal point. It has no per-item tint and no icon chip, so
 * a row of figures reads as one group instead of six competing boxes.
 *
 *   <StatStrip items={[
 *     { label: 'Contract',   value: '72.00', unit: 'M.T' },
 *     { label: '+ Addendum', value: null },            // renders "—"
 *     { label: '= JAF',      value: '0.00', unit: 'M.T', tone: 'warning', hint: 'expected 72.00' },
 *   ]} />
 *
 * `value: null | undefined` is the empty case and prints a muted em dash: a
 * figure that was never recorded must not look like a recorded zero.
 * `tone` is for meaning (a value that is off, late or blocked), never decoration.
 */
const TONES = {
  default: 'tw-text-slate-800',
  primary: 'tw-text-blue-700',
  success: 'tw-text-emerald-700',
  warning: 'tw-text-amber-700',
  danger:  'tw-text-red-600',
};

// Column counts must be literal class names — Tailwind scans source text and
// cannot see an interpolated `tw-grid-cols-${n}`.
const COLUMN_CLASSES = {
  1: 'tw-grid-cols-1',
  2: 'tw-grid-cols-2',
  3: 'tw-grid-cols-2 md:tw-grid-cols-3',
  4: 'tw-grid-cols-2 md:tw-grid-cols-4',
  5: 'tw-grid-cols-2 md:tw-grid-cols-5',
  6: 'tw-grid-cols-2 md:tw-grid-cols-6',
};

const StatStrip = ({ items, bordered = true, className = '' }) => {
  if (!items?.length) return null;

  const columns = COLUMN_CLASSES[Math.min(items.length, 6)] || COLUMN_CLASSES[6];

  return (
    <div
      className={[
        'tw-grid',
        columns,
        // divide-y is dropped from md up, where the items sit on one row.
        'tw-divide-x tw-divide-y md:tw-divide-y-0 tw-divide-slate-200',
        bordered ? 'tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white' : '',
        className,
      ].join(' ')}
    >
      {items.map(({ key, label, value, unit, hint, icon: Icon, tone = 'default', tooltip }, index) => {
        const isEmpty = value === null || value === undefined || value === '';

        return (
          <div key={key ?? (typeof label === 'string' ? label : index)} className="tw-px-4 tw-py-3 tw-min-w-0">
            <p className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-slate-500 tw-mb-1">
              {Icon && <Icon size={12} className="tw-text-slate-400 tw-flex-shrink-0" />}
              <span className="tw-truncate">{label}</span>
              {tooltip && (
                <span
                  title={tooltip}
                  aria-label={tooltip}
                  className="tw-inline-flex tw-flex-shrink-0 tw-text-slate-400 tw-cursor-help"
                >
                  <LuInfo size={11} />
                </span>
              )}
            </p>

            <p className="tw-mb-0 tw-flex tw-items-baseline tw-gap-1">
              <span className={`tw-text-base tw-font-semibold ${isEmpty ? 'tw-text-slate-300' : TONES[tone] || TONES.default}`}>
                {isEmpty ? '—' : value}
              </span>
              {!isEmpty && unit && <span className="tw-text-xs tw-text-slate-500">{unit}</span>}
            </p>

            {hint && <p className="tw-mt-0.5 tw-mb-0 tw-text-xs tw-text-slate-400 tw-truncate">{hint}</p>}
          </div>
        );
      })}
    </div>
  );
};

StatStrip.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    key:     PropTypes.string,
    label:   PropTypes.node.isRequired,
    value:   PropTypes.node,
    unit:    PropTypes.string,
    hint:    PropTypes.node,
    icon:    PropTypes.elementType,
    tone:    PropTypes.oneOf(Object.keys(TONES)),
    tooltip: PropTypes.string,
  })).isRequired,
  bordered:  PropTypes.bool,
  className: PropTypes.string,
};

export default StatStrip;
